<template>
    <div>
        <v-card class="client-transactions" outlined>
            <v-card-title class="page-heading primary">
                <v-row align="center">
                    <v-col cols="12" sm="9">
                        <v-btn
                            icon
                            dark
                            @click="$router.push({ name: 'adminClientTransactionsSummary' }).catch(err => {})"
                        >
                            <v-icon>fal fa-arrow-left</v-icon>
                        </v-btn>

                        {{ client ? client.name : '' }}
                    </v-col>
                    <v-col cols="12" sm="3" class="d-flex justify-end">
                        <v-btn
                            v-if="items.length > 0"
                            class="mr-3"
                            color="white"
                            outlined
                            @click="print()"
                        >
                            <v-icon left>fal fa-print</v-icon>
                            {{ $t('general.print') }}
                        </v-btn>

                        <v-btn
                            class="mr-2"
                            color="white"
                            outlined
                            @click="openForm()"
                        >
                            <v-icon left>fal fa-plus</v-icon>
                            {{ $t('general.add') }}
                        </v-btn>

                        <!-- <v-menu bottom transition="slide-y-transition">
                            <template v-slot:activator="{ on }">
                                <v-btn dark v-on="on" icon>
                                    <v-icon>far fa-ellipsis-v</v-icon>
                                </v-btn>
                            </template>

                            <v-list>
                                <v-list-item
                                    key="bankStatement"
                                    :disabled="true"
                                    @click=""
                                >
                                    <v-list-item-action class="mx-3">
                                        <v-icon :disabled="true">
                                            fal fa-university
                                        </v-icon>
                                    </v-list-item-action>
                                    <v-list-item-title>
                                        {{ $t('client_transactions.bank_statement_add') }}
                                    </v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu> -->
                    </v-col>
                </v-row>
            </v-card-title>

            <v-card-text>
                <client-transactions-search
                    :filter.sync="filter"
                />

                <v-data-table
                    class="items-table operations striped"
                    :headers="headers"
                    :items="items"
                    :options.sync="pagination"
                    :footer-props="{ 'items-per-page-options': [20, 50, 100, 200] }"
                    :items-per-page.sync="pagination.itemsPerPage"
                    :server-items-length="itemsTotal"
                    :loading="loading"
                    :mobile-breakpoint="0"
                    dense
                >
                    <template v-slot:item="{ item }">
                        <tr
                            :class="{
                                'font-weight-medium': true,
                                'error--text': item.getDebt().subtract(item.getCredit()).toUnit() > 0,
                                'success--text': item.getDebt().subtract(item.getCredit()).toUnit() < 0,
                            }"
                        >
                            <td>{{ item.number }}</td>
                            <td>{{ item.date | date }}</td>
                            <td>{{ item.due_date | date }}</td>
                            <td>{{ $t(`client_transactions.basis.${item.basis}`) }}</td>
                            <td>{{ item.document_number }}</td>
                            <td align="right">{{ item.getDebt().toFormat('0.00') }}</td>
                            <td align="right">{{ item.getCredit().toFormat('0.00') }}</td>
                            <td align="right">{{ item.getBalance().toFormat('0.00') }}</td>
                            <td align="center" class="w35 maxw60">
                                <v-menu bottom transition="slide-y-transition">
                                    <template v-slot:activator="{ on }">
                                        <v-btn v-on="on" icon>
                                            <v-icon>far fa-ellipsis-h</v-icon>
                                        </v-btn>
                                    </template>

                                    <v-list>
                                        <v-list-item
                                            key="edit"
                                            :disabled="item.type === 'auto'"
                                            @click="openForm(item)"
                                        >
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon :disabled="item.type === 'auto'">fal fa-pen</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title>
                                                {{ $t('general.edit') }}
                                            </v-list-item-title>
                                        </v-list-item>

                                        <v-list-item
                                            key="delete"
                                            class="red--text text--darken-1"
                                            @click="deleteItem(item)"
                                        >
                                            <v-list-item-action class="mr-4">
                                                <v-icon class="red--text">fal fa-times</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title class="red--text">
                                                {{ $t('general.delete') }}
                                            </v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <!-- Item form -->
        <client-transaction-edit
            :show.sync="showForm"
            :item="selectedItem"
            :client-id="client ? client.id : null"
            @save="onItemSave"
        />
    </div>
</template>

<script>
import { loadData } from '@/views/shared/loadPageData';
import ApiService from '@/services/ApiService';
import Client from '@/models/Client';
import ClientTransaction from '@/models/ClientTransaction';
import ClientTransactionsSearch from '@/views/shared/admin/clients/ClientTransactionsSearch';
import ClientTransactionEdit from '@/views/shared/admin/clients/ClientTransactionEdit';
import HtmlGen from '@/wrappers/HtmlGen';
import _ from 'lodash';

export default {
    components: {
        ClientTransactionsSearch,
        ClientTransactionEdit,
    },

    async created() {
        const data = await loadData(`/admin/load/client-transactions/${this.$route.params.client_id}`);

        if(data.client) {
            this.client = new Client(data.client);
            this.balance = new Client(data.balance);
        } else {
            this.$router.back();
        }
    },

    data() {
        return {
            items: [],
            itemsTotal: 0,
            loading: false,
            showForm: false,
            selectedItem: null,
            client: null,
            balance: null,

            filter: {
                clientId: this.$route.params.client_id,
                search: null,
                startDate: moment().startOf('year').format('YYYY-MM-DD') + ' 00:00:00',
                endDate: moment().format('YYYY-MM-DD') + ' 23:59:59',
            },

            headers: [
                { text: this.$tc('general.number'), value: 'number' },
                { text: this.$tc('general.date'), value: 'date' },
                { text: this.$t('client_transactions.due_date'), value: 'due_date' },
                { text: this.$t('client_transactions.basis.heading'), value: 'basis' },
                { text: this.$t('client_transactions.document_number'), value: 'document_number' },
                { text: this.$t('client_transactions.debt'), value: 'debt', align: 'right' },
                { text: this.$t('client_transactions.credit'), value: 'credit', align: 'right' },
                { text: this.$t('client_transactions.balance'), value: 'balance', align: 'right' },
                { text: '', sortable: false }
            ],

            pagination: {
                sortBy: ['number'],
                itemsPerPage: 100,
                page: 1,
                sortDesc: [true],
            },
        };
    },

    watch: {
        pagination: {
            handler() {
                this.getItems();
            },
            deep: true
        },

        filter() {
            this.getItems();
        },
    },

    methods: {
        getItems() {
            const { sortBy, sortDesc, page, itemsPerPage } = this.pagination;
            const sortField = sortBy[0] ? sortBy[0] : 'date';
            const sortOrder = sortDesc[0] ? 'descending' : 'ascending';

            let url = `/admin/client-transactions?page=${page}&itemsPerPage=${itemsPerPage}&sortField=${sortField}&sortOrder=${sortOrder}`;

            if (this.filter) {
                Object.keys(this.filter).forEach(key => {
                    if(this.filter[key]) {
                        url += `&${key}=${this.filter[key]}`;
                    }
                })
            }

            this.loading = true;

            ApiService.get(url).then(response => {
                this.loading = false;
                this.items = response.data.data.map(item => new ClientTransaction(item));
                this.itemsTotal = response.data.total;
            });
        },

        openForm(item = null) {
            this.selectedItem = item;
            this.showForm = true;
        },

        deleteItem(item) {
            this.$confirm(
                this.$t('client_transactions.permanently_delete'),
                {
                    buttonTrueText: this.$t('general.confirm'),
                    icon: 'fal fa-times',
                    color: 'error',
                    title: this.$t('general.delete')
                },
                result => {
                    if (!result) return;

                    ApiService.delete(`/admin/client-transactions/${item.id}`, {}).then(response => {
                        this.$snackbar(this.$t('client_transactions.deleted'), 'success');
                        this.getItems();
                    });
                }
            );
        },

        onItemSave(item) {
            this.getItems();
        },

        async print() {
            const sortField = 'number';
            const sortOrder = 'ascending';
            const itemsPerPage = 10000;
            const page = 1;
            const startDate = this.filter.startDate;
            const endDate = this.filter.endDate;

            let url = `/admin/client-transactions?page=${page}&itemsPerPage=${itemsPerPage}&sortField=${sortField}&sortOrder=${sortOrder}&clientId=${this.$route.params.client_id}`;

            const response = await ApiService.get(url);
            const items = response.data.data.map(item => new ClientTransaction(item));

            const html = ClientTransaction.toTextLines(items, this.client, startDate, endDate)
                .map(line => HtmlGen.fromTextLine(line)).join('');
            this.$printService.printCustom(null, html, null, { choosePrinter: true })
        },
    }
};
</script>

<style lang="scss" scoped>
.client-transactions {
    .page-heading {
        padding: 12px 16px;
        color: white;
        font-size: 16px;
        word-break: break-word;
    }

    ::v-deep .items-table {
        margin-top: 28px;

        th {
            font-size: 11px;
            padding: 0 4px;
            max-width: 80px;

            span {
                white-space: break-spaces;
            }
        }

        td {
            font-size: 13px;
            padding: 0 4px;
        }

        .separator {
            border-left: 1px solid rgba(0, 0, 0, 0.5);
            padding-left: 12px;
        }

        tfoot {
            font-weight: 500;
            background: #f3f4fb;
        }
    }
}
</style>
